import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'react-toastify';
import { ClipLoader } from 'react-spinners';
import { Helmet } from 'react-helmet';

import '../AiInterview/AIInterviewEnrolledCourse/Interview.css'
import '../AiInterview/AIInterviewEnrolledCourse/InterviewPayment.css'
import cardPayment from '../../Assets/card-payment2.png';
import { COURSES_URL, INTERVIEWMANAGEMENT_URL, JOBS_PORTAL_URL } from '../../Services/courseService';

const AIInterviewLandingUnenrolledCourse = () => {
    const [isAcknowledged, setIsAcknowledged] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [promoCode, setPromoCode] = useState('');
    const [discountAmount, setDiscountAmount] = useState(0);
    const [promoError, setPromoError] = useState('');
    const [attemptAllowed, setAttemptAllowed] = useState(false);
    const [totalAttempts, setTotalAttempts] = useState(0);
    const [loading, setLoading] = useState(false);
    const [buttonLoading, setButtonLoading] = useState(false);
    const [pricing, setPricing] = useState(0);
    const [attemptsGranted, setAttemptsGranted] = useState(0);
    const [paymentSettingsError, setPaymentSettingsError] = useState(false);
    const [isInitialCheckLoading, setIsInitialCheckLoading] = useState(true); // ✅ Initial load flag

    const navigate = useNavigate();
    const { courseName, experience } = useParams();

    const fullName = localStorage.getItem('fullName');
    const email = localStorage.getItem('email');
    const phoneNumber = localStorage.getItem('phoneNumber') || '';
    const userId = localStorage.getItem('userId');

    useEffect(() => {
        if (!userId) navigate('/login');
    }, [navigate, userId]);

    useEffect(() => {
    const fetchInitialData = async () => {
        try {
            setLoading(true);

            const [settingsRes, attemptsRes] = await Promise.all([
                axios.get(`${INTERVIEWMANAGEMENT_URL}/v1/interviews/admin/payment-settings`),
                axios.get(`${INTERVIEWMANAGEMENT_URL}/v1/interviews/attempts/remaining`, {
                    params: { userId: userId }
                })
            ]);

            setPricing(settingsRes.data.minAmountThreshold);
            setAttemptsGranted(settingsRes.data.attemptsGranted);
            setPaymentSettingsError(false);

            const message = attemptsRes.data;

            if (typeof message === "string" && message.includes("No record found for user")) {
                toast.error("You need to pay to attend.");
                setTotalAttempts(0);
                setAttemptAllowed(false);
                return;
            }

            const match = message.match(/(\d+)$/);
            const remainingAttempts = match ? parseInt(match[1], 10) : 0;

            setTotalAttempts(remainingAttempts);
            setAttemptAllowed(remainingAttempts > 0);

            if (remainingAttempts > 0) {
                toast.success(`You have ${remainingAttempts} attempt${remainingAttempts > 1 ? 's' : ''} left`);
            } else {
                toast.error("No attempts left. Please purchase more to continue.");
            }

        } catch (err) {
            console.error("Initial data fetch failed:", err);
            toast.error("Something went wrong while checking attempts.");
            setPaymentSettingsError(true);
        } finally {
            setLoading(false);
            setIsInitialCheckLoading(false);
        }
    };

    fetchInitialData();
}, [userId, courseName]);



    const handleCheckboxChange = () => {
        setIsAcknowledged(prev => !prev);
        setErrorMessage('');
    };

    const handleConfirm = () => {
        if (!isAcknowledged) {
            setErrorMessage('Please acknowledge the interview guidelines to proceed.');
        } else {
            navigate(`/instructor-interview-request`);
        }
    };

    const applyPromo = async () => {
        try {
            setPromoError('');
            const { data } = await axios.post(`${JOBS_PORTAL_URL}/api/promocode/apply`, {
                code: promoCode,
                userId,
                originalPrice: pricing
            });

            const discounted = data?.finalPrice;
            if (discounted < pricing) {
                const discount = pricing - discounted;
                setDiscountAmount(discount);
                toast.success(`Promo applied! You saved ₹${discount}`);
            } else {
                setDiscountAmount(0);
                setPromoError("Invalid or expired promo code.");
            }
        } catch (err) {
            setPromoError(err?.response?.data || "Promo code failed.");
        }
    };

    const totalAmount = pricing - discountAmount;

   const initiatePayment = async () => {
    try {
        setButtonLoading(true);
        const amount = Math.round(totalAmount);
        const { data } = await axios.post(`${COURSES_URL}/payments/create-order`, { amount });
        const { orderId } = data;

        const razorpay = new window.Razorpay({
            key: process.env.REACT_APP_RAZORPAY_KEY,
            amount: amount * 100,
            currency: "INR",
            order_id: orderId,
            prefill: { name: fullName, email, contact: phoneNumber },
            theme: { color: "#0B89C2" },
            handler: async function (response) {
    const { razorpay_payment_id, razorpay_order_id, razorpay_signature } = response;

    if (!razorpay_payment_id || !razorpay_order_id || !razorpay_signature) {
        toast.error("Payment verification failed.");
        return;
    }

    try {
        // 1. Verify payment
        await axios.post(`${COURSES_URL}/payments/verify-payment`, {
            razorpay_payment_id,
            razorpay_order_id,
            razorpay_signature
        });

        // 2. Save payment info
        await axios.post(`${COURSES_URL}/payments`, {
            courses: [{ courseId: null, courseName: `UnEnrolled ${courseName}- AI-Interview` }],
            skilltecheraUserId: userId,
            paymentId: razorpay_payment_id,
            orderId: razorpay_order_id,
            signature: razorpay_signature,
            totalAmount: amount,
            transactionDate: new Date().toISOString(),
            paymentType: "Razorpay",
            purchasePurpose: "AI_Interview_Payment",
            promoCode: promoCode || null
        });

        // 3. Add attempts
        await axios.post(`${INTERVIEWMANAGEMENT_URL}/v1/interviews/test/add-attempts`, null, {
            params: { userId, amount }
        });

        let attemptsUpdated = false;
        for (let i = 0; i < 5; i++) {
            await new Promise(resolve => setTimeout(resolve, 1000));

            const { data } = await axios.get(`${INTERVIEWMANAGEMENT_URL}/v1/interviews/attempts/remaining`, {
                params: { userId }
            });

            // You might need to adjust this logic based on what your API returns
            if (typeof data === "string" && (data.includes("Remaining attempts") || data.includes("No record found"))) {
                const match = data.match(/: (\d+)/);
                const remainingAttempts = match ? parseInt(match[1]) : 0;

                setAttemptAllowed(remainingAttempts > 0);
                setTotalAttempts(remainingAttempts);
                toast.success(`You have ${remainingAttempts} attempt${remainingAttempts !== 1 ? 's' : ''} left`);
                attemptsUpdated = true;
                break;
            }
        }

        if (!attemptsUpdated) {
            toast.warning("Payment successful, but attempts not updated yet. Try refreshing.");
        }
    } catch (err) {
        console.error("Error during payment flow:", err);
        toast.error("Something went wrong after payment.");
    }
}

        });

        razorpay.open();
    } catch (err) {
        console.error("Payment failed:", err);
        toast.error("Failed to initiate payment.");
    } finally {
        setButtonLoading(false);
        setLoading(false);
    }
};

    // ✅ Prevent UI flicker by waiting for initial load
    if (isInitialCheckLoading) {
        return (
            <div className="loading-overlay">
                <ClipLoader color="#333" loading={true} size={40} />
            </div>
        );
    }

    return (
        <div className="ai-interview-landing-container">
            <Helmet>
                <script type="application/ld+json">
                    {JSON.stringify({
                        "@context": "https://schema.org",
                        "@type": "Course",
                        "name": `UnEnrolled ${courseName} - AI Interview`,
                        "description": "AI-based technical interview to evaluate skills and confidence. Real-time camera/mic enabled mock environment. Get feedback and prepare for real jobs.",
                        "url": `https://skillzverse.com/unenrolled-ai-interview/${courseName}/${experience}`,
                        "provider": {
                            "@type": "Organization",
                            "name": "SkillzVerse",
                            "sameAs": "https://skillzverse.com"
                        }
                    })}
                </script>
            </Helmet>
            {loading && <div className="loading-overlay"><ClipLoader color="#333" loading={true} size={40} /></div>}

            {attemptAllowed ? (
                <div className="ai-interview-landing">
                    <h2>Instructor led Interview Instructions</h2>
                    <ul>
                        <li>Ensure a quiet, distraction-free environment.</li>
                        <li>Stable internet and working mic/webcam required.</li>
                        <li>Interview is recorded and remains confidential.</li>
                    </ul>
                    <p>By continuing, you confirm that you've read and understood the above guidelines.</p>
                    <div>
                        <input type="checkbox" id="acknowledge" checked={isAcknowledged} onChange={handleCheckboxChange} />
                        <label htmlFor="acknowledge" className="acknowledge-interview-label">
                            I acknowledge and agree to the interview guidelines.
                        </label>
                    </div>
                    {errorMessage && <p className="error-message">{errorMessage}</p>}
                    <button onClick={handleConfirm} disabled={!isAcknowledged}>Confirm</button>
                </div>
            ) : (
                <div className="interview-payment-main-container">
                    <div className="interview-payment-container">
                        <h1 className="payment-header">Unlock Attempts for Your AI Interview</h1>
                        <p>Get {attemptsGranted} attempt(s) for ₹{pricing}</p>

                        <div style={{ display: 'flex', alignItems: 'center', marginTop: '16px' }}>
                            <input
                                type="text"
                                placeholder="Enter Promo Code"
                                value={promoCode}
                                onChange={(e) => setPromoCode(e.target.value)}
                                style={{
                                    padding: '10px',
                                    fontSize: '16px',
                                    width: '250px',
                                    marginRight: '10px',
                                    border: '1px solid #ccc',
                                    borderRadius: '6px'
                                }}
                            />
                            <button
                                onClick={applyPromo}
                                style={{
                                    padding: '10px 20px',
                                    fontSize: '16px',
                                    backgroundColor: '#0B89C2',
                                    color: '#fff',
                                    border: 'none',
                                    borderRadius: '6px',
                                    cursor: 'pointer'
                                }}
                            >
                                Apply
                            </button>
                        </div>
                        {promoError && <p style={{ color: 'red', marginTop: '10px' }}>{promoError}</p>}
                        {discountAmount > 0 && <p style={{ color: 'green', marginTop: '10px' }}>Discount: ₹{discountAmount}</p>}

                        <button onClick={initiatePayment} className="pay-now-button">
                            {buttonLoading ? 'Processing...' : `Pay ₹${totalAmount}`}
                        </button>
                    </div>
                    <img src={cardPayment} alt="payment" className="interview-payment-img" />
                </div>
            )}
        </div>
    );
};

export default AIInterviewLandingUnenrolledCourse;